import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Laptop, HardDrive, Users } from "lucide-react";
import AssetManagementExtras from "../components/AssetManagementExtras";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const ASSET_TYPES = ["Laptop", "Desktop", "Monitor", "Phone", "Printer", "Other"];
const STATUSES = ["Available", "Assigned", "In Office Use", "In Repair", "Retired"];
const STATUS_COLOR = {
  "Available": "bg-emerald-100 text-emerald-700",
  "Assigned": "bg-blue-100 text-blue-700",
  "In Office Use": "bg-violet-100 text-violet-700",
  "In Repair": "bg-amber-100 text-amber-800",
  "Retired": "bg-slate-100 text-slate-600",
};

const emptyForm = () => ({
  asset_tag: "", auto_tag: true,
  asset_type: "", other_name: "",
  make_model: "", serial_no: "",
  assigned_to_id: "", assigned_to_name: "",
  assigned_on: "", purchase_date: "",
  asset_value: "", status: "Available",
});

export default function AssetsWorkflow() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  // v109.7 — Any user who reaches this page is either Master Admin or has
  // been explicitly granted access via the "Module Access" dialog. Both can
  // fully manage assets; only Master Admin can hard-delete.
  const canManage = true;
  const canDelete = isMaster;
  const [viewMode] = useGlobalViewMode();
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    try {
      const { data } = await api.get("/records/it_assets");
      setItems(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/directory").then(({ data }) => setPeople(data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((it) =>
      !q || [it.asset_tag, it.asset_type, it.other_name, it.make_model, it.serial_no, it.assigned_to_name, it.status]
        .some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [items, search]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (rec) => {
    setEditingId(rec.id);
    setForm({ ...emptyForm(), ...rec, auto_tag: !rec.asset_tag });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.asset_type) { toast.error("Asset Type is required"); return; }
    if (form.asset_type === "Other" && !form.other_name.trim()) {
      toast.error("Name the product is required when type is Other"); return;
    }
    if (!form.auto_tag && !form.asset_tag.trim()) {
      toast.error("Asset Tag is required when auto-tag is off"); return;
    }
    try {
      // If auto_tag is true, omit asset_tag so backend generates one
      const body = { ...form };
      if (body.auto_tag && !editingId) body.asset_tag = "";
      delete body.auto_tag;
      if (editingId) {
        await api.patch(`/records/it_assets/${editingId}`, body);
        toast.success("Asset updated");
      } else {
        const r = await api.post("/records/it_assets", body);
        toast.success(`Added · Tag ${r.data.asset_tag}`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/records/it_assets/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const isOther = form.asset_type === "Other";

  return (
    <div className="max-w-[1500px] space-y-6">
      <PageHeader
        moduleKey="it_assets"
        tone="sky"
        icon={HardDrive}
        eyebrow="IT Module"
        title="Asset Management"
        subtitle="Assign hardware to employees, track purchases and asset value."
        actions={
          <div className="flex items-center gap-2">
            {canManage && (
              <Button onClick={openCreate} className="bg-gradient-to-br from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white h-10 shadow-sm" data-testid="asset-add-button">
                <Plus size={16} className="mr-1.5" /> Add Asset
              </Button>
            )}
          </div>
        }
      />

      <AssetManagementExtras canApprove={canManage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          <div className="relative bg-gradient-to-br overflow-hidden rounded-t-lg from-sky-500 via-cyan-600 to-blue-700 px-6 py-5 text-white">
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            <div className="relative flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center">
                <HardDrive size={22} />
              </div>
              <div>
                <DialogTitle className="heading text-lg font-extrabold">{editingId ? "Edit" : "New"} Asset</DialogTitle>
                <div className="text-[11.5px] text-white/85 mt-0.5">Hardware, peripherals or other physical items issued by IT.</div>
              </div>
            </div>
          </div>
          <DialogHeader className="hidden"><DialogTitle>Form</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4">
            {!editingId && (
              <div className="md:col-span-2 flex items-center gap-3 px-3 py-2.5 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-lg">
                <Switch checked={form.auto_tag} onCheckedChange={(v) => setForm({ ...form, auto_tag: v })} data-testid="asset-auto-tag-toggle" />
                <div>
                  <div className="text-sm font-bold text-sky-900">Auto-generate Asset Tag</div>
                  <div className="text-[11px] text-sky-700/80">Backend will assign next tag like AST-2026-0001</div>
                </div>
              </div>
            )}
            {(!form.auto_tag || editingId) && (
              <F label={editingId ? "Asset Tag" : "Asset Tag *"}>
                <Input value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} placeholder="e.g. PV-LAP-001" data-testid="asset-tag" />
              </F>
            )}
            <F label="Asset Type *">
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger data-testid="asset-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{ASSET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {isOther && (
              <F label="Name the Product *" full>
                <Input value={form.other_name} onChange={(e) => setForm({ ...form, other_name: e.target.value })} placeholder="Describe the asset (e.g. UPS / Router…)" data-testid="asset-other-name" />
              </F>
            )}
            <F label="Make / Model">
              <Input value={form.make_model} onChange={(e) => setForm({ ...form, make_model: e.target.value })} data-testid="asset-make-model" />
            </F>
            <F label="Serial No.">
              <Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} data-testid="asset-serial" />
            </F>
            <F label="Assigned To">
              <Select
                value={form.assigned_to_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") { setForm({ ...form, assigned_to_id: "", assigned_to_name: "", status: form.status === "Assigned" ? "Available" : form.status }); return; }
                  const p = people.find((x) => x.id === v);
                  // Auto-set status → Assigned (locked) when an employee is chosen.
                  setForm({ ...form, assigned_to_id: v, assigned_to_name: p?.name || "", status: "Assigned" });
                }}
              >
                <SelectTrigger data-testid="asset-assigned-to"><SelectValue placeholder="Pick employee…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Unassigned —</SelectItem>
                  {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.employee_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Assigned On">
              <Input type="date" value={form.assigned_on} onChange={(e) => setForm({ ...form, assigned_on: e.target.value })} data-testid="asset-assigned-on" />
            </F>
            <F label="Purchase Date">
              <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} data-testid="asset-purchase-date" />
            </F>
            <F label="Value of the Asset (INR)">
              <Input value={form.asset_value} onChange={(e) => setForm({ ...form, asset_value: e.target.value })} placeholder="0" data-testid="asset-value" />
            </F>
            <F label={form.assigned_to_id ? "Status (locked — asset is assigned)" : "Status"}>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!!form.assigned_to_id}>
                <SelectTrigger data-testid="asset-status" className={form.assigned_to_id ? "bg-slate-50 cursor-not-allowed" : undefined}><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter className="px-6 pb-5">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-gradient-to-br from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white shadow-sm" data-testid="asset-submit">
              {editingId ? "Save Changes" : "Add Asset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tag, type, assignee, serial…" className="pl-9 h-10 bg-white border-sky-200 focus:border-sky-400" data-testid="asset-search" />
      </div>

      <div className="relative overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50/40 via-white to-cyan-50/30 overflow-x-auto" style={viewMode === "card" ? { display: "none" } : undefined}>
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-sky-200/30 rounded-full blur-3xl pointer-events-none" />
        <table className="relative w-full text-sm responsive-table">
          <thead className="border-b border-sky-200 bg-sky-50/60">
            <tr>
              <Th>Asset Tag</Th><Th>Type</Th><Th>Make / Model</Th>
              <Th>Assigned To</Th><Th>Assigned On</Th><Th>Purchase Date</Th>
              <Th>Value</Th><Th>Status</Th>
              {canManage && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={canManage ? 9 : 8} className="text-center py-12 text-slate-400 text-sm">No assets yet.</td></tr>
            )}
            {filtered.map((it) => (
              <tr key={it.id} className="border-b border-sky-100/60 hover:bg-white/60 transition-colors" data-testid={`asset-row-${it.asset_tag}`}>
                <td className="px-4 py-3 mono font-bold text-[12px] text-sky-700" data-label="Tag">{it.asset_tag}</td>
                <td className="px-4 py-3" data-label="Type">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center shadow-sm flex-shrink-0">
                      <Laptop size={11} className="text-white" />
                    </div>
                    <span>{it.asset_type === "Other" ? (it.other_name || "Other") : it.asset_type || "—"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Make / Model">{it.make_model || "—"}</td>
                <td className="px-4 py-3 text-[12px]" data-label="Assigned To">{it.assigned_to_name || <span className="text-slate-400 italic">unassigned</span>}</td>
                <td className="px-4 py-3 mono text-[11px]" data-label="Assigned On">{it.assigned_on || "—"}</td>
                <td className="px-4 py-3 mono text-[11px]" data-label="Purchase Date">{it.purchase_date || "—"}</td>
                <td className="px-4 py-3 mono text-[12px] font-bold text-emerald-700" data-label="Value">₹ {it.asset_value || "—"}</td>
                <td className="px-4 py-3" data-label="Status"><Badge className={`${STATUS_COLOR[it.status] || "bg-slate-100"} text-[10px] ring-1 ring-current/10`}>{it.status || "—"}</Badge></td>
                {canManage && (
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="h-8 text-xs bg-white border-sky-200 text-sky-700 hover:bg-sky-50" data-testid={`asset-edit-${it.asset_tag}`}>
                        <Pencil size={12} />
                      </Button>
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`asset-delete-${it.asset_tag}`}>
                              <Trash2 size={12} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {it.asset_tag}?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(it.id)} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="assets-card-grid">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 text-sm rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50/40 via-white to-cyan-50/30">
              No assets yet.
            </div>
          )}
          {filtered.map((it) => (
            <div key={it.id} className="relative overflow-hidden rounded-xl border border-sky-200 bg-gradient-to-br from-white via-sky-50/30 to-cyan-50/30 p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`asset-card-${it.asset_tag}`}>
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-sky-200/30 rounded-full blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center shadow-sm flex-shrink-0">
                      <Laptop size={16} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="mono text-[12.5px] font-bold text-sky-700 truncate">{it.asset_tag}</div>
                      <div className="text-[11px] text-slate-600 truncate">{it.asset_type === "Other" ? (it.other_name || "Other") : (it.asset_type || "—")}</div>
                    </div>
                  </div>
                  <Badge className={`${STATUS_COLOR[it.status] || "bg-slate-100"} text-[10px] ring-1 ring-current/10 shrink-0`}>{it.status || "—"}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-3">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Make / Model</div>
                    <div className="text-slate-800 truncate">{it.make_model || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Serial No.</div>
                    <div className="mono text-slate-800 truncate">{it.serial_no || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Assigned To</div>
                    <div className="text-slate-800 truncate">{it.assigned_to_name || <span className="text-slate-400 italic">unassigned</span>}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Assigned On</div>
                    <div className="mono text-slate-800">{it.assigned_on || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Purchase Date</div>
                    <div className="mono text-slate-800">{it.purchase_date || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Value</div>
                    <div className="mono font-bold text-emerald-700">₹ {it.asset_value || "—"}</div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-sky-100">
                    <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="h-8 text-[11px] bg-white border-sky-200 text-sky-700 hover:bg-sky-50" data-testid={`asset-card-edit-${it.asset_tag}`}>
                      <Pencil size={12} className="mr-1" /> Edit
                    </Button>
                    {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="h-8 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`asset-card-delete-${it.asset_tag}`}>
                            <Trash2 size={12} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {it.asset_tag}?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(it.id)} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-[0.16em] font-bold text-sky-700 ${className}`}>{children}</th>;
}
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
